import { insertAtIndex, move } from "@prismicio/editor-support/Array"
import { isDefined } from "@prismicio/editor-support/Type"
import {
  CompositeSliceContent,
  Document,
  isCompositeSliceContent,
  isSharedSliceContent,
  isSimpleSliceContent,
  SharedSliceContent,
  SimpleSliceContent,
  SliceContent,
  SliceItemContent,
  SlicesContent,
} from "@prismicio/types-internal/lib/content"
import {
  CompositeSlice,
  LegacySlice,
  type SharedSlice,
  type StaticSection,
  type StaticSlice,
  type Variation,
} from "@prismicio/types-internal/lib/customtypes"

import { createEmptySlice } from "./utils/createEmptySlice"
import { createSharedSlice } from "./utils/createSlice"
import { getFieldset } from "./utils/getFieldset"

interface GetSelectionSlicesArgs {
  section: StaticSection
  content: Document
}

export function getSectionSlices(args: GetSelectionSlicesArgs) {
  const { section, content } = args

  return Object.entries(content).flatMap((entry) => {
    const [zoneId, widget] = entry

    // This Document widget is not a slice zone: Ignore.
    if (widget.__TYPE__ !== "SliceContentType") return []

    const zoneConfig = section[zoneId]

    // The config does not know this slice zone: Ignore.
    if (!zoneConfig) return []

    // The config type changed at some point: Ignore.
    if (zoneConfig.type !== "Slices") return []

    return widget.value.flatMap<VisualSlice>((content, index) => {
      const config = zoneConfig?.config?.choices?.[content.name]
      if (!config) return []

      const info: SliceInfo = {
        zoneId,
        key: content.key,
        name: content.name,
        content: content.widget,
        config,
        canMoveUp: index !== 0,
        canMoveDown: index !== widget.value.length - 1,
      }

      return (
        asVisualSharedSlice(info) ??
        asVisualCompositeSlice(info) ??
        asVisualSimpleSlice(info) ??
        []
      )
    })
  })
}

export type SectionSliceConfig = {
  zoneId: string
  /**
   * The slice reference.
   * This value is pointing to the key on which the slice is defined.
   *
   * This value is available from the sliceConfig on sharedSlices but not for legacySlices.
   * Hence we are storing them to easily retrive them when working with the slice config.
   */
  sliceReference: string
  /**
   * The slice variation.
   * This slot is available to store the variation of the slice when working with a specific slice.
   */
  sliceVariation: string
  sliceConfig: StaticSlice
}

export function getSectionSliceConfigs(section: StaticSection) {
  return Object.entries(section).flatMap((entry) => {
    const [zoneId, config] = entry
    if (config.type !== "Slices") return []

    const choices = config.config?.choices ?? []

    return Object.entries(choices).flatMap<SectionSliceConfig>(
      ([key, sliceConfig]) => {
        return {
          zoneId,
          sliceReference: key,
          sliceConfig,
          sliceVariation: "",
        }
      },
    )
  })
}

export function moveSlice(
  content: Document,
  slice: VisualSlice,
  direction: "up" | "down",
): Document {
  return updateSliceZone(content, slice.zoneId, (zone) => {
    const sliceIndex = zone.value.findIndex((s) => s.key === slice.key)
    if (sliceIndex === -1) return zone

    return {
      ...zone,
      value: move(
        zone.value,
        sliceIndex,
        sliceIndex + (direction === "up" ? -1 : +1),
      ),
    }
  })
}

interface AddSliceResult {
  newContent: Document
  newSlice: SliceItemContent
}

export function addSlice(
  content: Document,
  config: SectionSliceConfig,
  index: number,
): AddSliceResult {
  const newSlice = createEmptySlice({ config: config })

  const newContent = updateSliceZone(content, config.zoneId, (zone) => {
    return {
      ...zone,
      value: insertAtIndex(zone.value, index, newSlice),
    }
  })

  return { newContent, newSlice }
}

export function addSlices(params: {
  content: Document
  slices: VisualSharedSlice[]
  index?: number
}) {
  const { slices, index } = params
  let { content } = params

  slices.forEach((slice, sliceIndex) => {
    content = updateSliceZone(content, slice.zoneId, (zone) => {
      const newSlice = createSharedSlice({
        sliceId: slice.config.id,
        uuid: slice.uuid,
        variationId: slice.variation.id,
        withContent: slice.content,
      })
      return {
        ...zone,
        value: insertAtIndex(
          zone.value,
          index === undefined ? zone.value.length : index + sliceIndex,
          newSlice,
        ),
      }
    })
  })
  return content
}

export function deleteSlice(content: Document, slice: VisualSlice): Document {
  return updateSliceZone(content, slice.zoneId, (zone) => ({
    ...zone,
    value: zone.value.filter((widget) => widget.key !== slice.key),
  }))
}

export function reorderSlices(
  content: Document,
  newOrder: VisualSlice[],
): Document {
  // Drag & Dropping across different slice zones is mental so we don't support it.
  // Also in most cases, there's a single slice zone per tab.
  const firstSliceZoneId = newOrder[0]?.zoneId
  if (firstSliceZoneId === undefined) return content

  return updateSliceZone(content, firstSliceZoneId, (zone) => ({
    ...zone,
    value: newOrder
      .map((slice) => zone.value.find((widget) => widget.key === slice.key))
      .filter(isDefined),
  }))
}

export function replaceVariation(
  content: Document,
  config: SliceConfigWithSharedSlice,
  originalSlice: VisualSharedSlice,
) {
  const {
    zoneId,
    sliceConfig: { id: sliceId },
    sliceVariation: variationId,
  } = config

  return updateSliceZone(content, zoneId, (zone) => {
    const newValue = zone.value.map((widget) => {
      const content = widget.widget

      if (content.__TYPE__ !== "SharedSliceContent") return widget
      if (widget.key !== originalSlice.key) return widget

      // Create a brand new slice but keep the UUID part of the key
      // the same so that we have UI continuity.
      return createSharedSlice({
        sliceId,
        variationId,
        uuid: originalSlice.uuid,
        withContent: content,
      })
    })

    return {
      ...zone,
      value: newValue,
    }
  })
}

interface ReplaceSliceArgs {
  content: Document
  slice: VisualSharedSlice
}

export function replaceSlice(args: ReplaceSliceArgs): AddSliceResult {
  const { content, slice } = args

  // Ideally, we could update the slice zone iterating over its value, finding
  // the slice with the same key and replacing its content.
  // There's a bug in the rich text field that makes this not work as expected:
  // The entire slice content must have a new reference for the RTF to pick up the change.
  // This is a workaround until the bug is fixed.

  // First, find the index of the slice to replace.
  const sliceZone = content[slice.zoneId]
  if (!sliceZone || sliceZone.__TYPE__ !== "SliceContentType")
    throw new Error("The slice zone doesn't exist.")

  const index = sliceZone.value.findIndex((s) => s.key === slice.key)
  if (index === -1) throw new Error("The slice doesn't exist.")

  // Then, delete the current slice.
  const updatedContent = deleteSlice(content, slice)
  // Finally, add the slice again with the new content.
  const newConfig: SectionSliceConfig = {
    zoneId: slice.zoneId,
    sliceConfig: slice.config,
    sliceVariation: slice.variation.id,
    sliceReference: slice.key,
  }
  const { newContent, newSlice } = addSlice(updatedContent, newConfig, index)
  newSlice.widget = slice.content
  return { newContent, newSlice }
}

export function updateSliceZone(
  content: Document,
  zoneId: string,
  updateFunction: (slicesContent: SlicesContent) => SlicesContent,
): Document {
  const zone = content[zoneId] ?? {
    __TYPE__: "SliceContentType" as const,
    value: [],
  }

  if (zone.__TYPE__ !== "SliceContentType") return content

  const updatedZone = updateFunction(zone)

  return {
    ...content,
    [zoneId]: updatedZone,
  }
}

export function getSliceUUIDFromKey(key: string) {
  return key.split("$")[1] ?? undefined
}

export function getSliceTitle(slice: VisualSlice) {
  if (slice.type === "shared") {
    return {
      title: slice.config.name,
      subTitle: slice.variation.name,
    }
  }

  if (slice.type === "composite" || slice.type === "simple") {
    return {
      title: slice.fieldset ?? slice.description,
      subTitle: "Legacy Slice",
    }
  }

  return { title: "", subTitle: "" }
}

export type VisualSlice =
  | VisualSharedSlice
  | VisualCompositeSlice
  | VisualSimpleSlice

export interface VisualSharedSlice {
  type: "shared"
  zoneId: string
  key: string
  name: string
  uuid: string
  variation: Variation
  content: SharedSliceContent
  config: SharedSlice
  canMoveUp: boolean
  canMoveDown: boolean
}

interface SliceInfo {
  zoneId: string
  key: string
  name: string
  content: SliceContent
  config: StaticSlice
  canMoveUp: boolean
  canMoveDown: boolean
}

function asVisualSharedSlice(args: SliceInfo): VisualSharedSlice | undefined {
  const { zoneId, key, content, config, name, canMoveUp, canMoveDown } = args

  if (!isSharedSliceContent(content) || config.type !== "SharedSlice")
    return undefined

  const variation = config.variations.find((v) => v.id === content.variation)
  if (!variation) return undefined

  const uuid = getSliceUUIDFromKey(key)
  if (!uuid) return undefined

  return {
    type: "shared",
    zoneId,
    key,
    name,
    uuid,
    config,
    variation,
    content,
    canMoveUp,
    canMoveDown,
  }
}

export interface VisualCompositeSlice {
  type: "composite"
  zoneId: string
  key: string
  name: string
  description: string
  fieldset: string | undefined
  uuid: string
  content: CompositeSliceContent
  canMoveUp: boolean
  canMoveDown: boolean
}

function asVisualCompositeSlice(
  args: SliceInfo,
): VisualCompositeSlice | undefined {
  const { zoneId, key, content, config, canMoveUp, canMoveDown, name } = args

  if (!isCompositeSliceContent(content) || config.type !== "Slice")
    return undefined

  const uuid = getSliceUUIDFromKey(key)
  if (!uuid) return undefined

  return {
    type: "composite",
    zoneId,
    key,
    name,
    uuid,
    content,
    description: config.description ?? "no description",
    fieldset: getFieldset({ config }),
    canMoveUp,
    canMoveDown,
  }
}

export interface VisualSimpleSlice {
  type: "simple"
  zoneId: string
  key: string
  name: string
  description: string
  fieldset: string | undefined
  uuid: string
  content: SimpleSliceContent
  canMoveUp: boolean
  canMoveDown: boolean
}

function asVisualSimpleSlice(args: SliceInfo): VisualSimpleSlice | undefined {
  const { zoneId, key, content, config, canMoveUp, canMoveDown, name } = args

  if (!isSimpleSliceContent(content)) return undefined

  const uuid = getSliceUUIDFromKey(key)
  if (!uuid) return undefined

  return {
    type: "simple",
    zoneId,
    key,
    name,
    uuid,
    content,
    description: config.type,
    fieldset: getFieldset({ config }),
    canMoveUp,
    canMoveDown,
  }
}

export interface SliceConfigWithSharedSlice extends SectionSliceConfig {
  sliceConfig: SharedSlice
}
export interface SliceConfigWithLegacySlice extends SectionSliceConfig {
  sliceConfig: CompositeSlice | LegacySlice
}

interface GetSliceConfigsArgs {
  sliceConfigs: SectionSliceConfig[] | undefined
}

export function getSharedSliceConfigs(props: GetSliceConfigsArgs) {
  const { sliceConfigs } = props

  if (!sliceConfigs) return []

  return sliceConfigs.filter(isSharedSliceConfig)
}

export function isSharedSliceConfig(
  config: SectionSliceConfig,
): config is SliceConfigWithSharedSlice {
  return config.sliceConfig.type === "SharedSlice"
}

export function getLegacySliceConfigs(props: GetSliceConfigsArgs) {
  const { sliceConfigs } = props

  if (!sliceConfigs) return []

  return sliceConfigs.filter(
    (config): config is SliceConfigWithLegacySlice =>
      config.sliceConfig.type !== "SharedSlice",
  )
}
